import type { TError, TPostWithAuthor } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

/**
 * API response shape for the scheduled-posts endpoint
 */
interface ScheduledPostsResponse {
  posts: TPostWithAuthor[];
}

/**
 * Query key for the viewer's own upcoming scheduled posts in a space
 */
export const getScheduledPostsQueryKey = (spaceId: string) => [ENTITY, 'posts', 'scheduled', spaceId];

/**
 * Query key for the viewer's own upcoming scheduled posts - hook version
 */
export const useGetScheduledPostsQueryKey = (spaceId: string) => getScheduledPostsQueryKey(spaceId);

/**
 * Hook to fetch the current user's own scheduled posts that haven't published
 * yet (hidden from the public feed). Powers the admin-only Scheduled view.
 *
 * @param spaceId - Feed sub-space ID (UUID)
 * @param enabled - Skip the request when the viewer can't schedule (non-admin)
 */
export const useGetScheduledPosts = (spaceId: string, enabled: boolean = true) => {
  return useQuery<ScheduledPostsResponse, TError>({
    queryKey: useGetScheduledPostsQueryKey(spaceId),
    queryFn: async () => {
      const res = await axios.get<ScheduledPostsResponse>(`/community-hub/spaces/${spaceId}/posts/scheduled`);
      return res.data;
    },
    enabled: !!spaceId && enabled,
    staleTime: 30 * 1000,
  });
};
